// errorhead-shaders.js — GLSL fragment shaders ported from eg0pr0xy/errorhead.
//
// Source: https://github.com/eg0pr0xy/errorhead
// Original shaders in services/glitchEngine/webgl/shaders.ts (GLSL ES 3.00)
// Canvas2D effects in services/glitchEngine/destruction.ts, post.ts
//
// Ported to the unstaglitch/pixeledit/framestack bridge pattern:
//   - u_texture  = input image (replaces u_source/u_prev from errorhead)
//   - u_time     = animation time (random per frame for still images)
//   - u_resolution = canvas dimensions
//   - u_flipY    = Y-flip for FBO vs canvas output
//   - v_uv       = texture coordinates [0,1]
//   - outColor   = fragment output
//
// Effects:
//   1. mosh      — Motion sculpture (flow warp, drift, rotation, zoom, diffusion)
//   2. post      — Post-processing (chroma, grain, vignette, contrast)
//   3. melt      — Vertical strip dropping (from destruction.ts applyMelt)
//   4. shred     — Horizontal slice displacement (from destruction.ts applyShred)
//   5. scatter   — Random chunk displacement (from destruction.ts applyScatter)
//   6. scanlines — CRT scanline overlay (from post.ts)
//   7. burn      — Heat tint overlay (from post.ts)
//   8. rgb-split — RGB channel separation (from post.ts)

var VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_position;
out vec2 v_uv;
uniform float u_flipY;
void main() {
  gl_Position = vec4(a_position.x, a_position.y * u_flipY, 0.0, 1.0);
  v_uv = a_position * 0.5 + 0.5;
}`;

// Header shared by all fragment shaders
var FRAG_HEADER = `#version 300 es
precision highp float;
uniform sampler2D u_texture;
uniform float u_time;
uniform vec2 u_resolution;
in vec2 v_uv;
out vec4 outColor;

// Shared noise/hash utilities (from errorhead shaders.ts)
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float luma(vec3 c) {
  return dot(c, vec3(0.299, 0.587, 0.114));
}
`;

// ---- setUniforms helpers ----
function setFloat(gl, locs, name, value) {
    if (locs[name]) gl.uniform1f(locs[name], value);
}
function setVec2(gl, locs, name, x, y) {
    if (locs[name]) gl.uniform2f(locs[name], x, y);
}

// ============================================================================
// 1. MOSH — Motion sculpture (ported from errorhead moshFrag)
// Flow warp + drift + rotation + zoom + diffusion. Single-frame adaptation:
// uses input as both prev and source; persistence/injection blend the warped
// result with the original.
// ============================================================================
var MOSH_FRAG = FRAG_HEADER + `
uniform float u_flowAmp;
uniform float u_flowScale;
uniform float u_flowSpeed;
uniform vec2 u_drift;
uniform float u_globalRot;
uniform float u_globalZoom;
uniform float u_diffusion;
uniform float u_persistence;
uniform float u_injection;
uniform float u_sculptThreshold;
uniform float u_sculptSoftness;

vec2 getFlow(vec2 p, float time) {
  float n1 = noise(p * u_flowScale + time * u_flowSpeed);
  float n2 = noise(p * u_flowScale + time * u_flowSpeed + 5.0);
  return vec2(n1 - 0.5, n2 - 0.5) * u_flowAmp;
}

void main() {
  vec2 uv = v_uv;
  vec4 src = texture(u_texture, uv);
  float l = luma(src.rgb);
  float t0 = clamp(u_sculptThreshold - u_sculptSoftness, 0.0, 1.0);
  float t1 = clamp(u_sculptThreshold + u_sculptSoftness, 0.0, 1.0);
  float sculpt = smoothstep(t0, t1, l);

  // Flow
  vec2 flow = getFlow(uv, u_time) * sculpt;

  // Global transforms (drift, rot, zoom)
  vec2 center = vec2(0.5);
  vec2 toCenter = uv - center;
  toCenter /= (1.0 + u_globalZoom * 0.01);
  float s = sin(u_globalRot);
  float c = cos(u_globalRot);
  toCenter = vec2(toCenter.x * c - toCenter.y * s, toCenter.x * s + toCenter.y * c);
  vec2 warpedUv = center + toCenter + flow + (u_drift * sculpt);
  vec2 finalUv = mix(uv, warpedUv, sculpt);

  // Diffusion: sample neighborhood
  vec4 prev;
  if (u_diffusion > 0.0) {
    vec2 off = u_diffusion / u_resolution;
    prev  = texture(u_texture, finalUv) * 0.4;
    prev += texture(u_texture, finalUv + vec2(off.x, 0.0)) * 0.15;
    prev += texture(u_texture, finalUv - vec2(off.x, 0.0)) * 0.15;
    prev += texture(u_texture, finalUv + vec2(0.0, off.y)) * 0.15;
    prev += texture(u_texture, finalUv - vec2(0.0, off.y)) * 0.15;
  } else {
    prev = texture(u_texture, finalUv);
  }

  // Blend persistence (prev) with injection (source)
  vec4 merged = mix(prev * u_persistence, src, u_injection);
  vec4 finalCol = mix(src, merged, sculpt);
  outColor = vec4(finalCol.rgb, 1.0);
}
`;

// ============================================================================
// 2. POST — Post-processing (ported from errorhead postFrag)
// Chromatic aberration + film grain + vignette + contrast curve.
// ============================================================================
var POST_FRAG = FRAG_HEADER + `
uniform float u_chroma;
uniform float u_grain;
uniform float u_vignette;
uniform float u_contrast;

void main() {
  // Chromatic aberration
  float r = texture(u_texture, v_uv + vec2(u_chroma, 0.0)).r;
  float g = texture(u_texture, v_uv).g;
  float b = texture(u_texture, v_uv - vec2(u_chroma, 0.0)).b;
  vec3 color = vec3(r, g, b);

  // Film grain
  float n = hash(v_uv + u_time);
  color += (n - 0.5) * u_grain;

  // Vignette
  float d = distance(v_uv, vec2(0.5));
  color *= smoothstep(0.8, 0.5, d * u_vignette);

  // Contrast (gamma/S-curve)
  color = pow(clamp(color, 0.0, 1.0), vec3(u_contrast));

  outColor = vec4(color, 1.0);
}
`;

// ============================================================================
// 3. MELT — Vertical strip dropping (ported from destruction.ts applyMelt)
// Image divided into vertical strips; each strip drops downward with sin-based
// wave modulation. Burn parameter adds random jitter to drop distance.
// ============================================================================
var MELT_FRAG = FRAG_HEADER + `
uniform float u_intensity;   // 0-1
uniform float u_stripWidth;  // pixels per strip
uniform float u_burn;        // 0-1, extra random jitter
uniform float u_warp;        // 0-1, wave modulation depth

void main() {
  vec2 uv = v_uv;
  float w = u_resolution.x;
  float stripCount = ceil(w / u_stripWidth);
  float stripW = w / stripCount;
  float stripIdx = floor(uv.x * w / stripW);

  // Per-strip speed and wave (deterministic from strip index)
  float speed = (sin(stripIdx * 99.1) + 1.5) * (u_intensity * 2.0);
  float wave = sin(u_time * speed + stripIdx * 0.2) * 0.5 + 0.5;

  // Drop distance in UV space
  float drop = (sin(stripIdx * 132.1 + u_time * speed) + 1.0) * u_intensity * 0.4;
  // Burn jitter
  drop += (hash(vec2(stripIdx, u_time)) - 0.5) * u_burn * 0.3;
  // Wave gate: only drop some strips at a time
  float gate = step(0.8, wave) + step(u_intensity * 0.8, hash(vec2(stripIdx, floor(u_time))));

  vec2 sampleUv = uv;
  sampleUv.y = fract(uv.y - drop * gate);

  vec4 col = texture(u_texture, sampleUv);

  // Burn tint (hard-light blend with orange)
  if (u_burn > 0.5) {
    vec3 orange = vec3(1.0, 0.42, 0.0);
    vec3 blended = 2.0 * col.rgb * orange;
    blended = mix(blended, 1.0 - 2.0 * (1.0 - col.rgb) * (1.0 - orange), step(0.5, col.r));
    col.rgb = mix(col.rgb, blended, (u_burn - 0.5) * 0.3);
  }

  outColor = vec4(col.rgb, 1.0);
}
`;

// ============================================================================
// 4. SHRED — Horizontal slice displacement (ported from destruction.ts applyShred)
// Image divided into horizontal slices; each slice shifts horizontally with
// noise-based displacement. Edges wrap.
// ============================================================================
var SHRED_FRAG = FRAG_HEADER + `
uniform float u_intensity;   // 0-1
uniform float u_sliceHeight; // pixels per slice
uniform float u_warp;        // 0-1, extra displacement depth

void main() {
  vec2 uv = v_uv;
  float h = u_resolution.y;
  float sliceCount = ceil(h / u_sliceHeight);
  float sliceH = h / sliceCount;
  float sliceIdx = floor(uv.y * h / sliceH);

  // Noise-based horizontal offset per slice
  float n = noise(vec2(sliceIdx * 20.2, u_time));
  float offX = (n - 0.5) * u_intensity * u_warp;

  vec2 sampleUv = uv;
  sampleUv.x = fract(uv.x + offX);

  vec4 col = texture(u_texture, sampleUv);
  outColor = vec4(col.rgb, 1.0);
}
`;

// ============================================================================
// 5. SCATTER — Random chunk displacement (ported from destruction.ts applyScatter)
// Random rectangular chunks are sampled from the source and displaced to new
// positions, with optional blend modes for color shifts.
// ============================================================================
var SCATTER_FRAG = FRAG_HEADER + `
uniform float u_intensity;   // 0-1
uniform float u_chunkCount;  // number of chunks
uniform float u_blend;       // 0-1, blend mode probability
uniform float u_scale;       // 0-1, chunk scale variation

// PRNG from hash
float rand(float s) { return hash(vec2(s, s * 1.7)); }

void main() {
  vec2 uv = v_uv;
  vec4 base = texture(u_texture, uv);

  // Accumulate displaced chunks
  vec3 acc = base.rgb;
  float accWeight = 1.0;

  for (float i = 0.0; i < 40.0; i++) {
    if (i >= u_chunkCount) break;
    float seed = u_time * 10.0 + i * 133.7;

    // Random chunk source position and size
    float sx = rand(seed) * 0.8;
    float sy = rand(seed + 1.0) * 0.8;
    float cw = (0.02 + rand(seed + 2.0) * 0.15) * (1.0 + u_scale * 0.5);
    float ch = (0.02 + rand(seed + 3.0) * 0.15) * (1.0 + u_scale * 0.5);

    // Displacement target
    float dx = sx + (rand(seed + 4.0) - 0.5) * u_intensity * 2.0;
    float dy = sy + (rand(seed + 5.0) - 0.5) * u_intensity * 1.0;

    // Check if this pixel is inside the displaced chunk
    if (uv.x >= dx && uv.x <= dx + cw && uv.y >= dy && uv.y <= dy + ch) {
      // Sample from source chunk
      vec2 srcUv = vec2(sx + (uv.x - dx), sy + (uv.y - dy));
      srcUv = clamp(srcUv, 0.0, 1.0);
      vec4 chunk = texture(u_texture, srcUv);

      // Blend mode selection
      float modeRand = rand(seed + 6.0);
      vec3 blended;
      if (modeRand > 0.85 && u_blend > 0.3) {
        blended = abs(acc - chunk.rgb);
      } else if (modeRand > 0.7 && u_blend > 0.3) {
        blended = min(vec3(1.0), acc + chunk.rgb * 0.8);
      } else if (modeRand > 0.5 && u_blend > 0.3) {
        blended = 1.0 - (1.0 - acc) * (1.0 - chunk.rgb);
      } else {
        blended = chunk.rgb;
      }
      acc = blended;
    }
  }

  outColor = vec4(acc, 1.0);
}
`;

// ============================================================================
// 6. SCANLINES — CRT scanline overlay (ported from post.ts)
// Dark horizontal lines with configurable spacing, intensity, and shimmer.
// ============================================================================
var SCANLINES_FRAG = FRAG_HEADER + `
uniform float u_spacing;    // pixels per scanline pair
uniform float u_intensity;  // 0-1, darkness
uniform float u_shimmer;    // 0-1, temporal shimmer

void main() {
  vec4 col = texture(u_texture, v_uv);
  float py = v_uv.y * u_resolution.y;
  float line = step(u_spacing * 0.5, mod(py, u_spacing));

  // Shimmer: slight time-based variation
  float shim = 1.0 - u_shimmer * 0.1 * sin(u_time * 5.0 + py * 0.1);
  float darken = mix(1.0, 1.0 - u_intensity * 0.6 * shim, line);

  outColor = vec4(col.rgb * darken, 1.0);
}
`;

// ============================================================================
// 7. BURN — Heat tint overlay (ported from post.ts burn)
// Orange/heat overlay with hard-light or overlay blending.
// ============================================================================
var BURN_FRAG = FRAG_HEADER + `
uniform float u_intensity;  // 0-1
uniform float u_tint;       // 0-1, 0=warm orange, 1=cool blue
uniform float u_blend;      // 0=overlay, 1=hard-light

void main() {
  vec4 col = texture(u_texture, v_uv);
  vec3 heatTint = mix(vec3(1.0, 0.42, 0.0), vec3(0.2, 0.5, 1.0), u_tint);
  float alpha = 0.08 + u_intensity * 0.25;

  // Overlay blend
  vec3 overlay = mix(
    2.0 * col.rgb * heatTint,
    1.0 - 2.0 * (1.0 - col.rgb) * (1.0 - heatTint),
    step(0.5, luma(col.rgb))
  );
  // Hard-light blend
  vec3 hardLight = mix(overlay, vec3(1.0) - 2.0 * (vec3(1.0) - col.rgb) * (vec3(1.0) - heatTint), step(0.5, luma(heatTint)));
  vec3 blended = mix(overlay, hardLight, u_blend);

  vec3 result = mix(col.rgb, blended, alpha);
  outColor = vec4(result, 1.0);
}
`;

// ============================================================================
// 8. RGB-SPLIT — RGB channel separation (ported from post.ts)
// Independent R/G/B channel offsets with screen blending.
// ============================================================================
var RGB_SPLIT_FRAG = FRAG_HEADER + `
uniform float u_redShift;
uniform float u_greenShift;
uniform float u_blueShift;
uniform float u_globalShift;
uniform float u_angle;  // 0=horizontal, 1=vertical, 0.5=diagonal

void main() {
  vec2 dir = normalize(vec2(mix(1.0, 0.0, u_angle), mix(0.0, 1.0, u_angle)) + vec2(0.001));
  float gs = u_globalShift;
  float rX = u_redShift - gs * 0.5;
  float gX = u_greenShift;
  float bX = u_blueShift + gs * 0.5;

  float r = texture(u_texture, v_uv + dir * rX).r;
  float g = texture(u_texture, v_uv + dir * gX).g;
  float b = texture(u_texture, v_uv + dir * bX).b;

  outColor = vec4(r, g, b, 1.0);
}
`;

// ---- EFFECT_SHADERS map ----
// Each entry: { fragment, setUniforms, linearFilter? }
var EFFECT_SHADERS = {
    'mosh': {
        fragment: MOSH_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_flowAmp', v.flowAmp);
            setFloat(gl, l, 'u_flowScale', v.flowScale);
            setFloat(gl, l, 'u_flowSpeed', v.flowSpeed);
            setVec2(gl, l, 'u_drift', v.driftX, v.driftY);
            setFloat(gl, l, 'u_globalRot', v.globalRot);
            setFloat(gl, l, 'u_globalZoom', v.globalZoom);
            setFloat(gl, l, 'u_diffusion', v.diffusion);
            setFloat(gl, l, 'u_persistence', v.persistence);
            setFloat(gl, l, 'u_injection', v.injection);
            setFloat(gl, l, 'u_sculptThreshold', v.sculptThreshold);
            setFloat(gl, l, 'u_sculptSoftness', v.sculptSoftness);
        },
        linearFilter: true
    },
    'post': {
        fragment: POST_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_chroma', v.chroma);
            setFloat(gl, l, 'u_grain', v.grain);
            setFloat(gl, l, 'u_vignette', v.vignette);
            setFloat(gl, l, 'u_contrast', v.contrast);
        },
        linearFilter: true
    },
    'melt': {
        fragment: MELT_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_intensity', v.intensity);
            setFloat(gl, l, 'u_stripWidth', v.stripWidth);
            setFloat(gl, l, 'u_burn', v.burn);
            setFloat(gl, l, 'u_warp', v.warp);
        },
        linearFilter: false
    },
    'shred': {
        fragment: SHRED_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_intensity', v.intensity);
            setFloat(gl, l, 'u_sliceHeight', v.sliceHeight);
            setFloat(gl, l, 'u_warp', v.warp);
        },
        linearFilter: false
    },
    'scatter': {
        fragment: SCATTER_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_intensity', v.intensity);
            setFloat(gl, l, 'u_chunkCount', v.chunkCount);
            setFloat(gl, l, 'u_blend', v.blend);
            setFloat(gl, l, 'u_scale', v.scale);
        },
        linearFilter: false
    },
    'scanlines': {
        fragment: SCANLINES_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_spacing', v.spacing);
            setFloat(gl, l, 'u_intensity', v.intensity);
            setFloat(gl, l, 'u_shimmer', v.shimmer);
        },
        linearFilter: false
    },
    'burn': {
        fragment: BURN_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_intensity', v.intensity);
            setFloat(gl, l, 'u_tint', v.tint);
            setFloat(gl, l, 'u_blend', v.blend);
        },
        linearFilter: true
    },
    'rgb-split': {
        fragment: RGB_SPLIT_FRAG,
        setUniforms: function(gl, l, v) {
            setFloat(gl, l, 'u_redShift', v.redShift);
            setFloat(gl, l, 'u_greenShift', v.greenShift);
            setFloat(gl, l, 'u_blueShift', v.blueShift);
            setFloat(gl, l, 'u_globalShift', v.globalShift);
            setFloat(gl, l, 'u_angle', v.angle);
        },
        linearFilter: true
    }
};

module.exports = {
    VERTEX_SHADER,
    EFFECT_SHADERS
};
